const $$splitComponentImporter = () => import('/app/apps/web/src/routes/_app/listings/$id.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
const $$splitLoaderImporter = () => import('/app/apps/web/src/routes/_app/listings/$id.tsx?tsr-split=loader');
import { lazyFn } from '@tanstack/react-router';
import { createFileRoute } from "@tanstack/react-router";
import { createTranslator } from "use-intl";
import { alternatesToLinks, buildCanonicalAlternates } from "@/i18n/alternates";
import { toAbsoluteUrl } from "@/lib/absolute-url";
import { jsonLdScripts } from "@/lib/jsonLdScripts";
import { withSiteTitle } from "@/lib/page-title";
import { buildReleaseFormatDisplay } from "@/lib/release-format-display";
import { buildSeoMetaTags, ROLLOUT_NOINDEX_ROBOTS } from "@/lib/seo-meta";
import { getRootLoaderData } from "@/types/intl";
import { buildAlbumImageUrl } from "@garimpa/lib/imageUrls";
interface ListingHeadRelease {
  title: string;
  artistDisplay: string;
  workId: string | null;
  mediaFormatDetails: MediaFormatDetail[];
}
interface ListingSeoFields {
  title: string;
  description?: string;
  imageUrl?: string;
}
function buildActiveListingSeo(release: ListingHeadRelease | null | undefined, tMeta: (key: "listingTitle" | "listingDescription", values: Record<string, string>) => string, tFormat: Parameters<typeof buildReleaseFormatDisplay>[1]["formatMessages"]): ListingSeoFields {
  if (!release) return {
    title: "Listing"
  };
  const format = buildReleaseFormatDisplay(release.mediaFormatDetails, {
    formatMessages: tFormat
  }).summaryText ?? "";
  const values = {
    title: release.title,
    artist: release.artistDisplay,
    format,
    hasFormat: format ? "yes" : "other"
  };
  return {
    title: tMeta("listingTitle", values),
    description: tMeta("listingDescription", values),
    imageUrl: toAbsoluteUrl(buildAlbumImageUrl(release.workId) ?? undefined) ?? undefined
  };
}
export const Route = createFileRoute("/_app/listings/$id")({
  loader: lazyFn($$splitLoaderImporter, 'loader'),
  head: async ({
    match,
    loaderData,
    params
  }) => {
    const {
      locale,
      messages
    } = getRootLoaderData(match);
    const tMeta = createTranslator({
      locale,
      messages,
      namespace: "globals.metadata"
    });
    const tFormat = createTranslator({
      locale,
      messages,
      namespace: "shared.formats"
    });
    const path = `/listings/${params.id}`;
    const alts = buildCanonicalAlternates(path);
    const listing = loaderData?.listing;
    const release = loaderData?.release;
    if (!listing) {
      return {
        meta: [{
          title: withSiteTitle(tMeta("listing"))
        }],
        links: alternatesToLinks(alts)
      };
    }
    if (listing.availability === "inactive") {
      const title = tMeta("listingUnavailable");
      return {
        meta: buildSeoMetaTags({
          title
        }, {
          robots: ROLLOUT_NOINDEX_ROBOTS,
          openGraph: false,
          twitter: false
        })
      };
    }
    const seo = buildActiveListingSeo(release, tMeta, tFormat);
    const url = alts.canonical;
    return {
      meta: buildSeoMetaTags({
        title: seo.title,
        description: seo.description,
        canonicalUrl: url,
        imageUrl: seo.imageUrl
      }, {
        openGraph: {
          type: "website"
        },
        twitter: {}
      }),
      links: alternatesToLinks(alts),
      scripts: jsonLdScripts((await import("@/utils/jsonld")).buildListingJsonLd(listing, loaderData.release, locale))
    };
  },
  component: lazyRouteComponent($$splitComponentImporter, 'component')
});